import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  acceptAllCookies,
  allowsAnalytics,
  allowsFunctional,
  allowsThirdParty,
  hasDecidedCookieConsent,
  readCookieConsent,
  rejectNonEssentialCookies,
  reportCookieConsentChoice,
  writeCookieConsent,
} from "../lib/cookieConsent";

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => readCookieConsent());
  const [bannerOpen, setBannerOpen] = useState(() => !hasDecidedCookieConsent());
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    const onExternal = (e) => {
      if (e?.detail) setConsent(e.detail);
    };
    window.addEventListener("agdysa:cookie-consent", onExternal);
    return () => window.removeEventListener("agdysa:cookie-consent", onExternal);
  }, []);

  const persist = useCallback((partial) => {
    const next = writeCookieConsent(partial);
    setConsent(next);
    setBannerOpen(false);
    setPrefsOpen(false);
    const allOn = next.functional && next.analytics && next.thirdParty;
    const allOff = !next.functional && !next.analytics && !next.thirdParty;
    reportCookieConsentChoice(allOn ? "accepted" : allOff ? "rejected" : "customized");
    return next;
  }, []);

  const acceptAll = useCallback(() => {
    const next = acceptAllCookies();
    setConsent(next);
    setBannerOpen(false);
    setPrefsOpen(false);
    reportCookieConsentChoice("accepted");
  }, []);

  const rejectAll = useCallback(() => {
    const next = rejectNonEssentialCookies();
    setConsent(next);
    setBannerOpen(false);
    setPrefsOpen(false);
    reportCookieConsentChoice("rejected");
  }, []);

  const openPreferences = useCallback(() => {
    setPrefsOpen(true);
    setBannerOpen(true);
  }, []);

  const value = useMemo(
    () => ({
      consent,
      bannerOpen,
      prefsOpen,
      setBannerOpen,
      setPrefsOpen,
      openPreferences,
      acceptAll,
      rejectAll,
      savePreferences: persist,
      decided: hasDecidedCookieConsent(consent),
      thirdPartyAllowed: allowsThirdParty(consent),
      functionalAllowed: allowsFunctional(consent),
      analyticsAllowed: allowsAnalytics(consent),
    }),
    [consent, bannerOpen, prefsOpen, openPreferences, acceptAll, rejectAll, persist]
  );

  return (
    <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useCookieConsent debe usarse dentro de CookieConsentProvider");
  }
  return ctx;
}

export function useCookieConsentOptional() {
  return useContext(CookieConsentContext);
}
